import clsx from "clsx";
import { useEffect, useRef } from "react";
import type { AssistenteIAContextoTela, AssistenteIAMensagem } from "../types";
import AppAssistenteIAInformativo from "./AppAssistenteIAInformativo";
import AppAssistenteIASugestoes from "./AppAssistenteIASugestoes";
import MessageBubble from "./MessageBubble";
import ThinkingBubble from "./ThinkingBubble";

type AppAssistenteIAMensagensProps = {
	readonly aberto: boolean;
	readonly expandido: boolean;
	readonly contexto: AssistenteIAContextoTela;
	readonly mensagens: AssistenteIAMensagem[];
	readonly enviando: boolean;
	readonly limpando: boolean;
	readonly onEnviarMensagem: (conteudo: string) => void;
};

export default function AppAssistenteIAMensagens({
	aberto,
	expandido,
	contexto,
	mensagens,
	enviando,
	limpando,
	onEnviarMensagem,
}: AppAssistenteIAMensagensProps) {
	const fimMensagensRef = useRef<HTMLDivElement | null>(null);
	const exibirApresentacao = mensagens.length === 0;
	const exibirSugestoes = exibirApresentacao && !!contexto.perguntasSugeridas?.length;

	useEffect(() => {
		if (!aberto) return;
		fimMensagensRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
	}, [aberto, enviando, mensagens.length]);

	function handleSugestao(perguntaSugerida: string) {
		if (enviando || limpando) return;
		onEnviarMensagem(perguntaSugerida);
	}

	return (
		<div className="min-h-0 flex-1 overflow-y-auto bg-muted/20 p-8">
			<div
				className={clsx(
					"mx-auto flex w-full max-w-5xl flex-col gap-4",
					expandido && "border rounded-2xl p-8 h-full",
				)}
			>
				{exibirApresentacao
					? (contexto.conteudoInformativo ?? (
							<AppAssistenteIAInformativo
								titulo={`Como posso ajudar em ${contexto.titulo}?`}
								descricao="Faça perguntas sobre a tela atual para interpretar informações, revisar parâmetros e apoiar próximos passos."
							/>
						))
					: null}

				{mensagens.map((mensagem) => (
					<MessageBubble key={mensagem.id} mensagem={mensagem} />
				))}

				{enviando ? <ThinkingBubble /> : null}

				{exibirSugestoes ? (
					<AppAssistenteIASugestoes
						perguntas={contexto.perguntasSugeridas ?? []}
						enviando={enviando || limpando}
						onSelecionar={handleSugestao}
					/>
				) : null}

				<div ref={fimMensagensRef} />
			</div>
		</div>
	);
}
